import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Cell
} from "recharts";
import "./responsive.css";

// ===== OPTIMIZED: Import from modules =====
import { theme } from './theme';
import { resolveProviderKey, getProviderColor, displayProviderName } from './utils/providerUtils';

// ===== OPTIMIZED: Import types from module =====
import type { Metric, Provider, FrameworkMode, SovereigntyProfilesData, ProviderSovereigntyResult } from './types';
import { SOV_DOMAINS } from './types';

// ===== OPTIMIZED: Import helper functions from modules =====
import { clamp, computeScores, groupByCategory, buildDomainRadar, groupBySovDomain, computeDomainScore, computeSovereigntyResults } from './utils/calculations';

// ===== OPTIMIZED: Import PDF export service =====
import { generatePdfBase64 } from './services/pdfExport';

// ===== OPTIMIZED: Import i18n =====
import { useLanguage, getTranslatedString } from './i18n';

// ===== Import sovereignty constants =====
import { SOV_DOMAIN_LABELS, SEAL_LABELS } from './utils/sovereigntyConstants';

// ===== OPTIMIZED: Import new components (ready for integration) =====
import { EmailModal } from './components/EmailModal';
import { Header } from './components/Header';
import { SidebarMenuItem } from './components/Sidebar';
import { sendReport } from './services/apiClient';
import {
  ProviderSelectionStep,
  CloudActStep,
} from './components/questionnaire';

// ===== Import sovereignty UI components =====
import { ProfileSelector } from './components/ProfileSelector';
import { C3ADownloadButton } from './components/C3ADownloadButton';
import { DomainScoreCard } from './components/DomainScoreCard';
import { SealBadge } from './components/SealBadge';

/** Sort metrics grouped by SOV domain: SOV-1..SOV-8 first, then null/"Business" at end.
 *  Within each domain group, sort by weight descending. */
function sortMetricsBySovDomain(metrics: Metric[], weights: Record<string, number>): Metric[] {
  const domainOrder = SOV_DOMAINS as readonly string[];
  return [...metrics].sort((a, b) => {
    const aDomain = a.sovDomain;
    const bDomain = b.sovDomain;
    // null domains go to the end
    const aIdx = aDomain ? domainOrder.indexOf(aDomain) : domainOrder.length;
    const bIdx = bDomain ? domainOrder.indexOf(bDomain) : domainOrder.length;
    if (aIdx !== bIdx) return aIdx - bIdx;
    // Within same domain, sort by weight descending
    const wa = weights[a.id] ?? (a.weightDefault ?? 3);
    const wb = weights[b.id] ?? (b.weightDefault ?? 3);
    return wb - wa;
  });
}

/** Component */
type SovereignCloudConnectorProps = {
  onOpenRegulatory?: () => void;
  onBack?: () => void;
  onSwitchToWorkplace?: () => void;
};

export default function SovereignCloudConnector(
  { onOpenRegulatory, onBack, onSwitchToWorkplace }: SovereignCloudConnectorProps
) {
  // Language hook
  const { language } = useLanguage();
  
  const [qStage, setQStage] = useState<
    'framework-selection' | 'providers' | 'cloud-act' | 'it-landscape' | 'questions-cloud-usage' | 'questions'>('framework-selection');
  const [itInfra, setItInfra] = useState<'onprem' | 'private' | 'hybrid' | 'public' | null>(null);
  const [cloudActRelevant, setCloudActRelevant] = useState<boolean | null>(null);

  // Welche Cloud-Anbieter nutzt der Kunde heute?
  const [currentCloudUsage, setCurrentCloudUsage] = useState<Record<string, boolean>>({});

  // Falls der Nutzer sagt "anderer Cloud Provider"
  const [otherCloudInUse, setOtherCloudInUse] = useState(false);

  // Temporäre Auswahl nur für den Fragebogen-Schritt 1
  const [qProviderSelection, setQProviderSelection] = useState<Record<string, boolean>>({});
  const [showIntro, setShowIntro] = useState(false);
  // Data
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [weights, setWeights] = useState<Record<string, number>>({});

  // Provider-Filter (Standard: alle aktiv)
  const [enabledProviders, setEnabledProviders] = useState<Record<string, boolean>>({});

  // Sovereignty profile state
  const [frameworkMode, setFrameworkMode] = useState<FrameworkMode>("EU-CSF");
  const [profilesData, setProfilesData] = useState<SovereigntyProfilesData | null>(null);

  // Loading/Error + Cache-bust
  const [, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [cacheBust] = useState(0);

  // View state
  const [view, setView] = useState<"bars" | "radar" | "heatmap">("bars");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Questionnaire / Wizard
  const [qOpen, setQOpen] = useState(true);

  useEffect(() => {
    console.log("DEBUG: qStage geändert auf →", qStage);
  }, [qStage]);

  const [qIndex, setQIndex] = useState(0);
  const [qOrder, setQOrder] = useState<Metric[]>([]);

  // Auto-build question order when data loads and questionnaire is open
  useEffect(() => {
    if (qOpen && metrics.length > 0 && qOrder.length === 0) {
      const order = sortMetricsBySovDomain(metrics, weights);
      setQOrder(order);
      setQIndex(0);
    }
  }, [qOpen, metrics, weights, qOrder.length]);

  // Startet den Fragebogen als eigene Seite
  const startQuestionnaire = () => {
    // Start immer mit Schritt 1 (Provider)
    setShowIntro(false);
    setQOpen(true);
    setQStage('framework-selection');

    // Vorauswahl: alle Provider starten als NICHT ausgewählt
    const initial = Object.fromEntries(
      providers.map(p => [p.id, false])
    );
    setQProviderSelection(initial);

    // Fragenreihenfolge (bauen wir hier schon vor)
    const order = sortMetricsBySovDomain(metrics, weights);
    setQOrder(order);
    setQIndex(0);
  };

  // Schließen-Funktion
  const closeQuestionnaire = () => {
    setQOpen(false);
  };

  // Info-Dialog für Merkmalsbeschreibung
  const [infoMetric, setInfoMetric] = useState<Metric | null>(null);

  // Email modal state for lead-gen flow
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  const sidebarMenuItems: SidebarMenuItem[] = [
    ...(onBack ? [{ labelKey: 'sidebar.backToModeSelector', onClick: onBack }] : []),
    ...(onSwitchToWorkplace ? [{ labelKey: 'sidebar.switchToWorkplace', onClick: onSwitchToWorkplace }] : []),
    { labelKey: 'sidebar.questionnaire', onClick: startQuestionnaire },
    {
      labelKey: 'sidebar.reset',
      onClick: () => {
        setWeights(Object.fromEntries(metrics.map(x => [x.id, clamp(x.weightDefault ?? 3, 1, 5)])));
        setEnabledProviders(Object.fromEntries(providers.map(p => [p.id, true])));
      },
    },
    { labelKey: 'sidebar.exportReport', onClick: () => setEmailModalOpen(true) },
    { labelKey: 'sidebar.regulatory', onClick: () => onOpenRegulatory?.() },
  ];

  const barCaptureRef = useRef<HTMLDivElement>(null);
  const radarCaptureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (qOpen) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [qOpen]);

  // Load JSONs from /public (with cache-buster)
  useEffect(() => {
    async function load() {
      setLoading(true);
      setErrorMsg(null);
      try {
        const mRes = await fetch(`/metrics.json?t=${Date.now()}&b=${cacheBust}`, { cache: "no-store" });
        const pRes = await fetch(`/providers.json?t=${Date.now()}&b=${cacheBust}`, { cache: "no-store" });
        const spRes = await fetch(`/sovereignty_profiles.json?t=${Date.now()}&b=${cacheBust}`, { cache: "no-store" });

        if (!mRes.ok) throw new Error(`metrics.json HTTP ${mRes.status}`);
        if (!pRes.ok) throw new Error(`providers.json HTTP ${pRes.status}`);
        if (!spRes.ok) throw new Error(`sovereignty_profiles.json HTTP ${spRes.status}`);

        const m: Metric[] = await mRes.json();
        const p: Provider[] = await pRes.json();
        const sp: SovereigntyProfilesData = await spRes.json();
        if (!Array.isArray(m)) throw new Error("metrics.json ist kein Array");
        if (!Array.isArray(p)) throw new Error("providers.json ist kein Array");
        if (!sp || !sp.profiles) throw new Error("sovereignty_profiles.json hat kein gültiges Format");

        setMetrics(m);
        setProviders(p);
        setProfilesData(sp);
        setEnabledProviders(Object.fromEntries(p.map((prov) => [prov.id, true])));
        setWeights(Object.fromEntries(m.map((x) => [x.id, clamp(x.weightDefault ?? 3, 1, 5)])));
      } catch (e: any) {
        console.error("Failed to load data:", e);
        setErrorMsg(e?.message || String(e));
        setMetrics([]);
        setProviders([]);
        setProfilesData(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [cacheBust]);

  const activeProviders = useMemo(
    () => providers.filter((pr) => enabledProviders[pr.id] !== false),
    [providers, enabledProviders]
  );

  const results = useMemo(() => {
    if (!metrics.length || !activeProviders.length) return [];
    return computeScores(metrics, activeProviders, weights, currentCloudUsage, cloudActRelevant ?? false);
  }, [metrics, activeProviders, weights, currentCloudUsage, cloudActRelevant]);

  const sovereigntyResults = useMemo((): ProviderSovereigntyResult[] => {
    if (!metrics.length || !activeProviders.length || !profilesData) return [];

    // Determine profile weights based on framework mode
    let profileWeights: Record<string, number>;
    if (frameworkMode === 'NO_FRAMEWORK_C5') {
      // Simple scoring doesn't use domain weights — equal weights
      profileWeights = { 'SOV-1': 0.125, 'SOV-2': 0.125, 'SOV-3': 0.125, 'SOV-4': 0.125, 'SOV-5': 0.125, 'SOV-6': 0.125, 'SOV-7': 0.125, 'SOV-8': 0.125 };
    } else if (frameworkMode === 'C3A') {
      const profile = profilesData.profiles['DE-C3A'];
      if (!profile) return [];
      profileWeights = profile.domainWeights;
    } else {
      // EU-CSF
      const profile = profilesData.profiles['EU-CSF'];
      if (!profile) return [];
      profileWeights = profile.domainWeights;
    }

    // Get hard requirements for C3A mode
    const hardReqs = frameworkMode === 'C3A' && profilesData.hardRequirements?.['C3A']
      ? profilesData.hardRequirements['C3A']
      : undefined;

    return computeSovereigntyResults(
      metrics,
      activeProviders,
      weights,
      profileWeights,
      currentCloudUsage,
      cloudActRelevant ?? false,
      frameworkMode,
      hardReqs
    );
  }, [metrics, activeProviders, weights, profilesData, frameworkMode, currentCloudUsage, cloudActRelevant]);

  const top = results[0];

  // Radar-Daten für die PDF-Erfassung (unabhängig vom sichtbaren Tab)
  const radarForCapture = useMemo(() => {
    if (!sovereigntyResults.length) {
      return { radarData: [], rankedProviders: [] as { id: string; name: string }[] };
    }
    return buildDomainRadar(sovereigntyResults, 3);
  }, [sovereigntyResults]);


  // ===== Email Report Handler (lead-gen flow) =====
  const handleEmailReportSubmit = async (email: string, reportName: string) => {
    const pdfBase64 = await generatePdfBase64({
      metrics,
      activeProviders,
      weights,
      currentCloudUsage,
      barCaptureRef,
      radarCaptureRef,
    });

    const result = await sendReport({
      email,
      pdfBase64,
      consent: true,
      consentTimestamp: new Date().toISOString(),
      language,
      providerCount: activeProviders.length,
    });

    if (!result.success) {
      throw new Error(result.message || 'Report sending failed');
    }
  };

  // Background wrapper for questionnaire pages
  const withCloudBg = (content: React.ReactNode) => (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      <div style={{ position: 'fixed', inset: 0, backgroundImage: 'url(/clouds.png)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(3px)', zIndex: 0 }} />
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.55)', zIndex: 0 }} />
      <div style={{ position: 'relative', zIndex: 1 }}>{content}</div>
    </div>
  );

  // === Questionnaire als eigene Seite ===
  // === Questionnaire Schritt 0: Framework-Auswahl ===
  if (qOpen && qStage === 'framework-selection') {
    const modes = profilesData?.frameworkModes;
    return withCloudBg(
      <div style={{ minHeight: '100vh', background: 'transparent', color: theme.text, fontFamily: 'Arial, sans-serif' }}>
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} menuItems={sidebarMenuItems} />
        <div style={{ height: 64 }} />
        <section style={{ maxWidth: 700, margin: '0 auto', padding: 16 }}>
          <div style={{ background: 'rgba(255,255,255,0.95)', border: `1px solid ${theme.border}`, borderRadius: 12, padding: 32 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: theme.text }}>
              {language === 'de' ? 'Bewertungsmodus wählen' : 'Select Assessment Mode'}
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: theme.muted }}>
              {language === 'de'
                ? 'Wählen Sie das Framework, nach dem die Cloud-Anbieter bewertet werden sollen.'
                : 'Choose the framework for evaluating cloud providers.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {modes && Object.entries(modes).map(([id, mode]) => {
                const isActive = frameworkMode === id;
                const label = language === 'en' && (mode as any).labelEn ? (mode as any).labelEn : (mode as any).label ?? id;
                const desc = language === 'en' && (mode as any).descriptionEn ? (mode as any).descriptionEn : (mode as any).description ?? '';
                return (
                  <button
                    key={id}
                    onClick={() => setFrameworkMode(id as FrameworkMode)}
                    style={{
                      padding: '16px 20px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                      border: isActive ? `2px solid ${theme.secondary}` : `1px solid ${theme.border}`,
                      background: isActive ? `${theme.secondary}10` : '#fff',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 12, color: theme.muted, lineHeight: 1.4 }}>{desc}</div>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setQStage('providers')}
              style={{
                marginTop: 24, padding: '12px 32px', border: `1px solid ${theme.primaryBorder}`,
                background: theme.primarySoft, color: theme.text, fontWeight: 700, fontSize: 15,
                borderRadius: 6, cursor: 'pointer', width: '100%',
              }}
            >
              {language === 'de' ? 'Weiter' : 'Continue'}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // === Questionnaire Schritt 1: Provider-Auswahl ===
  if (qOpen && qStage === 'providers') {
    const proceedFromProviders = () => {
      // Übernehme Auswahl in den globalen Filter
      const map = Object.fromEntries(
        providers.map(p => [p.id, qProviderSelection[p.id] === true])
      );
      setEnabledProviders(map);

      // Nun zu Schritt 2: Cloud Act Frage
      setQStage('cloud-act');
    };

    return withCloudBg(
      <ProviderSelectionStep
        providers={providers}
        selection={qProviderSelection}
        onToggle={(id) =>
          setQProviderSelection((s) => ({ ...s, [id]: !s[id] }))
        }
        onProceed={proceedFromProviders}
        onClose={closeQuestionnaire}
      />
    );
  }


  /** Fragebogen – Cloud Act Frage */
  if (qOpen && qStage === 'cloud-act') {
    return withCloudBg(
      <CloudActStep
        value={cloudActRelevant}
        onChange={(val) => setCloudActRelevant(val)}
        onProceed={() => setQStage('it-landscape')}
        onClose={() => {
          setQOpen(false);
          setQStage('providers');
        }}
      />
    );
  }

  /** Fragebogen – Schritt IT-Landschaft + aktuelle Cloud-Nutzung */
  if (qOpen && qStage === 'it-landscape') {
    return withCloudBg(
      <div
        style={{
          minHeight: "100vh",
          background: 'transparent',
          color: theme.text,
          fontFamily: "Arial, sans-serif",
        }}
      >
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} menuItems={sidebarMenuItems} />

        {/* Abstand */}
        <div style={{ height: 64 }} />

        {/* Haupt-Container */}
        <section
          style={{
            maxWidth: 900,
            margin: "0 auto",
            padding: 16,
            background: "rgba(255,255,255,0.9)",
            border: `1px solid ${theme.border}`,
          }}
        >
          {/* Frage 1: IT-Landschaft */}
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>
            Wie sieht Ihre aktuelle IT-Infrastruktur aus?
          </h2>

          <p style={{ marginTop: 6, color: theme.muted }}>
            Ihre Antwort hilft uns, die nachfolgenden Fragen besser einzuordnen.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 12,
              marginTop: 20,
            }}
          >
            {[
              { key: "onprem", label: "On-Premise" },
              { key: "private", label: "Private Cloud" },
              { key: "hybrid", label: "Hybrid Cloud" },
              { key: "public", label: "Public Cloud" },
            ].map((opt) => (
              <label
                key={opt.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  border: `1px solid ${theme.border}`,
                  background:
                    itInfra === opt.key ? theme.primarySoft : "rgba(255,255,255,0.6)",
                }}
              >
                <input
                  type="radio"
                  checked={itInfra === opt.key}
                  onChange={() => setItInfra(opt.key as any)}
                  style={{ accentColor: theme.primary }}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          {/* Frage 2: Aktuelle Cloud-Nutzung – nur wenn nicht On-Premise */}
          {itInfra && itInfra !== "onprem" && (
            <div style={{ marginTop: 28 }}>
              <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                Welche Cloud-Anbieter haben Sie heute im Einsatz?
              </h3>
              <p style={{ marginTop: 4, color: theme.muted, fontSize: 15 }}>
                Mehrfachauswahl möglich. Diese Angabe fließt als Bonus in die Bewertung ein.
              </p>

              {providers.length === 0 ? (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 15,
                    color: theme.muted,
                    border: `1px solid ${theme.border}`,
                    padding: 10,
                  }}
                >
                  Keine Anbieter geladen. Prüfe <code>providers.json</code>.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 10,
                    marginTop: 12,
                  }}
                >
                  {providers.map((p) => {
                    const checked = !!currentCloudUsage[p.id];

                    // Hyperscaler identifizieren
                    const isHyperscaler = ['AWS', 'Microsoft', 'Google'].includes(resolveProviderKey(p.name));

                    // Deaktivieren falls Private Cloud
                    const disabled = itInfra === 'private' && isHyperscaler;

                    return (
                      <label
                        key={p.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          borderRadius: 6,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          background: checked ? theme.primarySoft : 'transparent',
                          opacity: disabled ? 0.4 : 1,
                          pointerEvents: disabled ? 'none' : 'auto',
                          transition: 'background 0.2s ease',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) => {
                            if (!disabled) {
                              setCurrentCloudUsage((prev) => ({
                                ...prev,
                                [p.id]: e.target.checked,
                              }));
                            }
                          }}
                          style={{ accentColor: theme.primary }}
                        />

                        <span style={{ fontWeight: 600 }}>{displayProviderName(p.name)}</span>
                      </label>
                    );
                  })}

                  {/* Anderer Cloud Provider */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 6,
                      cursor: "pointer",
                      border: `1px solid ${theme.border}`,
                      background: otherCloudInUse
                        ? theme.primarySoft
                        : "rgba(255,255,255,0.8)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={otherCloudInUse}
                      onChange={(e) => setOtherCloudInUse(e.target.checked)}
                      style={{ accentColor: theme.primary }}
                    />
                    <span style={{ fontWeight: 600 }}>Anderer Cloud Provider</span>
                  </label>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Footer (Weiter) */}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "rgba(255,255,255,0.9)",
            borderTop: `1px solid ${theme.border}`,
            padding: "12px 24px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            backdropFilter: "blur(6px)",
          }}
        >
          <button
            disabled={!itInfra}
            onClick={() => {
              // On-Premise: direkt in die Fragen
              // Private/Hybrid/Public: ebenfalls, aber mit gesetzten currentCloudUsage-Boni
              setQStage("questions");
              setQIndex(0);
            }}
            style={{
              border: `1px solid ${theme.primaryBorder}`,
              background: itInfra ? theme.primarySoft : "#f3f4f6",
              padding: "8px 14px",
              cursor: itInfra ? "pointer" : "not-allowed",
              fontWeight: 700,
            }}
          >
            Weiter
          </button>
        </div>
      </div>
    ); // end withCloudBg for it-landscape
  }

  // === Questionnaire Schritt 2: Fragen (mit Slide-Animation) ===
  if (qOpen && qStage === 'questions') {
    const total = qOrder.length;

    const progress = total
      ? Math.round(((qIndex + 1) / total) * 100)
      : 0;

    const goToIndex = (idx: number) => {
      if (idx < 0 || idx >= total) return;
      setQIndex(idx);
    };

    const next = () => {
      if (qIndex < total - 1) setQIndex(qIndex + 1);
      else setQOpen(false);
    };

    const prev = () => {
      if (qIndex > 0) setQIndex(qIndex - 1);
    };

    const skip = () => next();

    // slideWidthPercent & sideOffsetPercent ENTFALLEN komplett
    return withCloudBg(
      <div
        style={{
          minHeight: '100vh',
          background: 'transparent',
          color: theme.text,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} menuItems={sidebarMenuItems} />

        {/* Abstand für Header */}
        <div style={{ height: 64 }} />

        {/* Hauptcontainer */}
        <main
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '16px 0 80px',
          }}
        >

          {/* Slider-Container: zeigt Nachbar-Karten leicht an */}
          <div
            style={{
              overflow: 'hidden',
              padding: '0 24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                // WICHTIG:
                // - keine gap in px → saubere Prozentrechnung
                // - pro Schritt um 90% verschieben, aber mit +5% Offset,
                //   damit die aktive Karte immer zentriert ist
                transform: `translateX(-${qIndex * 100}%)`,
                transition: 'transform 0.4s ease',
              }}
            >
              {qOrder.map((metric, idx) => {
                const value =
                  weights[metric.id] ??
                  clamp(metric.weightDefault ?? 3, 1, 5);
                const active = idx === qIndex;

                return (
                  <div
                    key={metric.id}
                    style={{
                      flex: '0 0 100%',
                      maxWidth: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '0 1vw',
                      boxSizing: 'border-box',
                      borderRadius: 3,
                      opacity: active ? 1 : 0.45,
                      transform: active ? 'scale(1)' : 'scale(0.97)',
                      transition: 'opacity 0.25s ease, transform 0.25s ease',

                      // WICHTIG: Höhe anpassen
                      minHeight: 'calc(100vh - 64px - 70px)',
                    }}
                  >
                    <div
                      style={{
                        border: `1px solid ${theme.border}`,
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.95)',
                        padding: 24,
                        minHeight: 260,
                        boxShadow: active
                          ? '0 16px 40px rgba(0,0,0,0.18)'
                          : '0 8px 20px rgba(0,0,0,0.08)',
                      }}
                    >
                      {/* SOV Domain header – show when this is the first metric in a new domain group */}
                      {(() => {
                        const prevDomain = idx > 0 ? qOrder[idx - 1].sovDomain : undefined;
                        const curDomain = metric.sovDomain;
                        if (curDomain !== prevDomain) {
                          const domainLabel = curDomain
                            ? SOV_DOMAIN_LABELS[curDomain]
                            : null;
                          const headerText = curDomain
                            ? `${curDomain}: ${domainLabel ? domainLabel[language] || domainLabel.en : curDomain}`
                            : (language === 'de' ? 'Business' : 'Business');
                          return (
                            <div
                              style={{
                                marginBottom: 14,
                                padding: '8px 12px',
                                background: curDomain ? 'linear-gradient(135deg, #e0e7ff 0%, #f0f4ff 100%)' : 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
                                borderRadius: 6,
                                borderLeft: `4px solid ${curDomain ? theme.primary : '#f59e0b'}`,
                              }}
                            >
                              <span style={{ fontSize: 13, fontWeight: 700, color: curDomain ? theme.primary : '#92400e', letterSpacing: 0.3 }}>
                                {headerText}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {/* Frage-Header in der Karte */}
                      <div
                        style={{
                          marginBottom: 16,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            marginBottom: 8,
                            color: theme.muted,
                          }}
                        >
                          Frage {qIndex + 1} von {total}
                          {metric?.category ? (
                            <> · Kategorie: <b>{metric.category}</b></>
                          ) : null}
                        </div>

                        {/* Fortschrittsbalken */}
                        <div
                          style={{
                            height: 8,
                            background: theme.successSoft,
                            border: `1px solid ${theme.successBorder}`,
                            overflow: 'hidden',
                            borderRadius: 4,
                          }}
                        >
                          <div
                            style={{
                              width: `${progress}%`,
                              height: '100%',
                              background: theme.primary,
                              transition: 'width 0.25s ease',
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 10,
                        }}
                      >
                        <h2
                          style={{
                            margin: 0,
                            fontSize: 24,
                            fontWeight: 800,
                            maxWidth: '80%',
                          }}
                        >
                          {getTranslatedString(metric.label, language)}
                        </h2>
                        <button
                          onClick={() => setInfoMetric(metric)}
                          title={`Mehr Infos zu ${getTranslatedString(metric.label, language)}`}
                          style={{
                            border: `1px solid ${theme.border}`,
                            borderRadius: 9999,
                            width: 28,
                            height: 28,
                            lineHeight: '26px',
                            textAlign: 'center',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: 12,
                            color: theme.muted,
                          }}
                        >
                          i
                        </button>
                      </div>

                      <p
                        style={{
                          fontSize: 16,
                          color: theme.muted,
                          marginTop: 0,
                          marginBottom: 18,
                          lineHeight: 1.5,
                        }}
                      >
                        {(metric.description &&
                          metric.description.trim().length > 0
                          ? metric.description
                          : metric.question) || ''}
                      </p>

                      {/* Slider + Stufen-Buttons */}
                      <div>
                        <input
                          type="range"
                          min={1}
                          max={5}
                          step={1}
                          value={value}
                          onChange={(e) =>
                            setWeights((w) => ({
                              ...w,
                              [metric.id]: Number(e.target.value),
                            }))
                          }
                          className="weight-slider"
                          style={{ width: '100%' }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 12,
                            color: theme.muted,
                            marginTop: 4,
                          }}
                        >
                          <span>1 (gering)</span>
                          <span>3 (mittel)</span>
                          <span>5 (sehr wichtig)</span>
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              onClick={() =>
                                setWeights((w) => ({
                                  ...w,
                                  [metric.id]: n,
                                }))
                              }
                              style={{
                                border: `1px solid ${n === value
                                  ? theme.primaryBorder
                                  : theme.border
                                  }`,
                                background:
                                  n === value ? theme.primarySoft : '#fff',
                                padding: '8px 14px',
                                fontSize: 15,
                                cursor: 'pointer',
                                borderRadius: 3,
                              }}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>

        {/* Fixierter Navigations-Footer mit Punkten */}
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.95)',
            borderTop: `1px solid ${theme.border}`,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            zIndex: 2100,
            backdropFilter: 'blur(6px)',
          }}
        >
          {/* Links: Zurück / Überspringen */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={prev}
              disabled={qIndex === 0}
              style={{
                border: `1px solid ${theme.border}`,
                background: '#fff',
                padding: '6px 10px',
                cursor: qIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: qIndex === 0 ? 0.5 : 1,
                borderRadius: 3,
                fontSize: 15,
              }}
            >
              Zurück
            </button>
            <button
              onClick={skip}
              style={{
                border: `1px solid ${theme.border}`,
                background: '#fff',
                padding: '6px 10px',
                cursor: 'pointer',
                borderRadius: 3,
                fontSize: 15,
              }}
            >
              Überspringen
            </button>
          </div>

          {/* Mitte: Punkte */}
          <div
            className="question-dots"
            style={{
              display: 'flex',
              gap: 6,
              flex: 1,
              justifyContent: 'center',
            }}
          >
            {qOrder.map((metric, idx) => {
              const answered = !!weights[metric.id];
              const active = idx === qIndex;
              return (
                <button
                  key={metric.id}
                  onClick={() => goToIndex(idx)}
                  style={{
                    width: active ? 14 : 10,
                    height: active ? 14 : 10,
                    borderRadius: 9999,
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    background: active
                      ? theme.primary
                      : answered
                        ? '#9ca3af'
                        : '#e5e7eb',
                    opacity: active ? 1 : answered ? 0.9 : 0.7,
                  }}
                  aria-label={`Gehe zu Frage ${idx + 1}`}
                />
              );
            })}
          </div>

          {/* Rechts: Weiter / Fertigstellen */}
          <div>
            <button
              onClick={next}
              style={{
                border: `1px solid ${theme.primaryBorder}`,
                background: theme.primarySoft,
                padding: '6px 14px',
                cursor: 'pointer',
                borderRadius: 3,
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              {qIndex < total - 1 ? 'Weiter' : 'Fertigstellen'}
            </button>
          </div>
        </div>
      </div>
    ); // end withCloudBg for questions
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: '#F4F4F4',
        color: theme.text,
        padding: 16,
        fontFamily: "Arial, sans-serif",
        position: 'relative',
      }}
    >
      {/* Background image with white overlay and blur */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: 'url(/clouds.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(3px)',
        zIndex: 0,
        animation: 'bgFadeIn 2.5s ease-out forwards',
      }} />
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255,255,255,0.55)',
        zIndex: 0,
        animation: 'overlayFadeIn 2.5s ease-out forwards',
      }} />
      <style>{`
        @keyframes bgFadeIn { from { filter: blur(0px); } to { filter: blur(3px); } }
        @keyframes overlayFadeIn { from { background: rgba(255,255,255,0); } to { background: rgba(255,255,255,0.55); } }
      `}</style>
      <div style={{ position: 'relative', zIndex: 1 }}>
      {showIntro && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: '#F4F4F4',
          }}
        >
          {/* Overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.05) 100%)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />

          {/* Content */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "grid",
              gap: 32,
              height: "100%",
              color: "#fff",

              /* Standard: Desktop */
              gridTemplateColumns: "1fr auto",
              padding: "15vh 6vw",
              textAlign: "left",
              alignItems: "center",
            }}
            className="intro-content"
          >
            {/* Text-Bereich */}
            <div className="intro-text">
              <h1
                style={{
                  margin: 0,
                  fontSize: "clamp(28px, 5vw, 56px)",
                  lineHeight: 1.05,
                  fontWeight: 800,
                  letterSpacing: 0.2,
                  textShadow: "0 2px 16px rgba(0,0,0,0.35)",
                }}
              >
                Sovereign Cloud&nbsp;Vergleich
              </h1>

              <p
                style={{
                  marginTop: 14,
                  maxWidth: 780,
                  fontSize: "clamp(14px, 2vw, 18px)",
                  lineHeight: 1.6,
                  opacity: 0.95,
                  textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                }}
              >
                Diese interaktive Demo vergleicht die Angebote für souveräne Cloud-Plattformen der führenden
                Cloud-Anbieter im Hinblick auf digitale Souveränität. Sie bewertet relevante Kriterien wie Kontrolle,
                Compliance und Kontinuität anhand transparenter Metriken. Das Ziel ist es, eine schnelle, objektive
                Orientierung für souveräne Cloud-Strategien zu bieten.
              </p>

              <p
                style={{
                  marginTop: 14,
                  maxWidth: 780,
                  fontSize: "clamp(14px, 2vw, 18px)",
                  lineHeight: 1.6,
                  opacity: 0.95,
                  textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                }}
              >
                Diese Anwendung befindet sich in Entwicklung. Inhalte können unvollständig sein.
                Ich erhebe keinen Anspruch auf Richtigkeit oder Vollständigkeit.
              </p>
            </div>

            {/* Button-Bereich */}
            <div className="intro-cta">
              <button
                onClick={startQuestionnaire}
                style={{
                  appearance: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "16px 28px",
                  borderRadius: 3,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  background: "#FF6A4C",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
                  transition: "transform .08s ease, box-shadow .12s ease, opacity .2s ease",
                }}
                onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
                onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                aria-label="Demo starten"
              >
                Jetzt starten
              </button>
            </div>
          </div>

          {/* Intro-Footer */}
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              padding: "10px 14px calc(env(safe-area-inset-bottom) + 10px)",
              display: "flex",
              justifyContent: "center",
              zIndex: 10001,
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                background: "rgba(0,0,0,0.5)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                padding: "8px 14px",
                borderRadius: 3,
                fontSize: 15,
                letterSpacing: 0.3,
                backdropFilter: "blur(6px)",
              }}
            >
              Powered by{" "}
              <a
                href="https://www.crayon.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#fff", textDecoration: "underline", margin: "0 4px" }}
              >
                Crayon
              </a>
              &nbsp;&amp;&nbsp;
              <a
                href="https://www.softwareone.com/de-de"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#fff", textDecoration: "underline", margin: "0 4px" }}
              >
                SoftwareOne
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ===== PAGE WRAPPER ===== */}
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          background: "transparent",
          border: "none",
          padding: 0,
          color: theme.text,
        }}
      >
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} menuItems={sidebarMenuItems} />

        {/* Spacer für fixed Header */}
        <div style={{ height: 64 }} />

        {/* Zur Übersicht Button */}
        {onBack && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px 0' }}>
            <button
              onClick={onBack}
              style={{
                border: `1px solid ${theme.border}`,
                background: '#fff',
                padding: '6px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                color: theme.text,
              }}
            >
              Zur Übersicht
            </button>
          </div>
        )}

        {/* Fehleranzeige */}
        {errorMsg && (
          <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 12 }}>
            Fehler: {errorMsg}
          </div>
        )}

        {/* ===== CONTENT LAYOUT ===== */}
        <div className="layout">
          {/* Ergebnisse – links (breit) auf Desktop, oben auf Mobile */}
          <div
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 3,
              padding: 12,
              background: theme.cardBg,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Ergebnisse</div>

            {/* Profile Selector + C3A Download */}
            {profilesData && profilesData.frameworkModes && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  marginBottom: 12,
                  flexWrap: 'wrap',
                }}
              >
                <ProfileSelector
                  activeMode={frameworkMode}
                  frameworkModes={profilesData.frameworkModes}
                  onChange={setFrameworkMode}
                />
                <C3ADownloadButton />
              </div>
            )}

            {/* Provider-Filter */}
            {providers.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                  Anbieter auswählen
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {providers.map((p) => {
                    const checked = enabledProviders[p.id] !== false;
                    return (
                      <label
                        key={p.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12,
                          border: `1px solid ${checked ? theme.primaryBorder : theme.border
                            }`,
                          background: checked ? theme.primarySoft : "#fff",
                          color: theme.text,
                          borderRadius: 6,
                          padding: "6px 10px",
                          cursor: "pointer",
                          transition: "all .15s ease",
                          boxShadow: checked ? "0 1px 0 rgba(0,0,0,0.03)" : "none",
                        }}
                        onMouseEnter={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.06)")
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.boxShadow = checked
                          ? "0 1px 0 rgba(0,0,0,0.03)"
                          : "none")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setEnabledProviders((prev) => ({
                              ...prev,
                              [p.id]: e.target.checked,
                            }))
                          }
                          style={{ accentColor: theme.primary }}
                        />
                        <span>{p.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {!providers.length && (
              <div style={{ fontSize: 15, color: theme.muted }}>
                Noch keine Anbieter geladen. Prüfe{" "}
                <code>/public/providers.json</code> und klicke „JSON neu laden“.
              </div>
            )}

            {!!providers.length && (() => {
              // Radar-Daten: use SOV domain axes from sovereignty results
              const { radarData, rankedProviders } = sovereigntyResults.length > 0
                ? buildDomainRadar(sovereigntyResults, 3)
                : { radarData: [], rankedProviders: [] as { id: string; name: string }[] };

              return (
                <>
                  {/* Tabs: Ranking / Heatmap (Radar ist immer oben rechts sichtbar) */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <button
                      style={{
                        border: `1px solid ${view === "bars" ? theme.primaryBorder : theme.border
                          }`,
                        borderRadius: 3,
                        padding: "6px 10px",
                        background: view === "bars" ? theme.primarySoft : "#fff",
                        cursor: "pointer",
                      }}
                      onClick={() => setView("bars")}
                    >
                      Ranking
                    </button>
                  </div>

                  {/* Charts-Reihe: links Balken (mit Ranking), rechts Radar */}
                  <div className="charts-row" style={{ marginBottom: 12 }}>
                    {/* LINKER BLOCK: Balken + Legende + Ranking-Liste */}
                    <div>
                      <div className="chart-bars" style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={results}
                            margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                          >
                            <XAxis dataKey="name" hide />
                            <YAxis domain={[0, 100]} hide />
                            <Tooltip
                              formatter={(v: number, _name: string, props: any) => [
                                `${v}%`,
                                props.payload.name,
                              ]}
                              contentStyle={{
                                background: "#fff",
                                border: `1px solid ${theme.border}`,
                                borderRadius: 3,
                                fontSize: 12,
                                color: theme.text,
                              }}
                              cursor={{ fill: "rgba(0,0,0,0.05)" }}
                            />
                            <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                              {results.map((r) => (
                                <Cell key={r.id} fill={getProviderColor(r.name)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Legende */}
                      <div
                        className="legend-grid"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                          gap: 8,
                          margin: "0 0 12px",
                        }}
                      >
                        {results.map((r) => (
                          <div
                            key={r.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 9999,
                                background: getProviderColor(r.name),
                                display: "inline-block",
                                border: "1px solid rgba(0,0,0,0.1)",
                                flex: "0 0 auto",
                              }}
                            />
                            <span
                              style={{
                                wordBreak: "break-word",
                                lineHeight: 1.2,
                              }}
                            >
                              {r.name}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Ranking-Liste NUR wenn Ranking-Tab aktiv */}
                      {view === "bars" && (
                        <>
                          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            {results.map((r, i) => (
                              <li
                                key={r.id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  border: `1px solid ${theme.border}`,
                                  borderRadius: 3,
                                  padding: "6px 8px",
                                  marginBottom: 6,
                                  background: "#fff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 9999,
                                      background: getProviderColor(r.name),
                                      display: "inline-block",
                                    }}
                                  />
                                  <span>
                                    <span
                                      style={{
                                        width: 20,
                                        display: "inline-block",
                                        textAlign: "right",
                                      }}
                                    >
                                      {i + 1}.
                                    </span>{" "}
                                    {r.name}
                                  </span>
                                </div>
                                <div style={{ fontWeight: 700 }}>{r.score}%</div>
                              </li>
                            ))}
                          </ul>

                          {top && (
                            <div
                              style={{
                                marginTop: 8,
                                background: theme.primarySoft,
                                border: `1px solid ${theme.primaryBorder}`,
                                padding: 8,
                                borderRadius: 3,
                                textAlign: "center",
                              }}
                            >
                              <b>Top Match:</b> {top.name} ({top.score}%)
                            </div>
                          )}

                        </>
                      )}
                    </div>

                    {/* RECHTER BLOCK: Radar + Legende */}
                    <div>
                      {/* RADAR-CHART (SOV Domain Axes) */}
                      <div className="chart-radar" style={{ width: "100%", height: 340 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={radarData}>
                            <PolarGrid stroke={theme.border} />
                            <PolarAngleAxis
                              dataKey="metric"
                              tick={{ fill: theme.text, fontSize: 12 }}
                              tickFormatter={(t: string) => {
                                const domainLabel = SOV_DOMAIN_LABELS[t];
                                if (domainLabel) {
                                  const label = domainLabel[language] || domainLabel.en;
                                  const shortLabel = label.split(' ')[0];
                                  return `${t} ${shortLabel}`;
                                }
                                return t.length > 22 ? t.slice(0, 22) + "…" : t;
                              }}
                            />
                            <PolarRadiusAxis
                              angle={30}
                              domain={[0, 10]}
                              tick={{ fill: theme.muted, fontSize: 10 }}
                              stroke={theme.border}
                            />
                            {rankedProviders.map((p) => (
                              <Radar
                                key={p.id}
                                name={p.name}
                                dataKey={p.name}
                                stroke={getProviderColor(p.name)}
                                fill={getProviderColor(p.name)}
                                fillOpacity={theme.radarFillOpacity}
                                strokeOpacity={0.9}
                              />
                            ))}
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* PROVIDER LEGENDE */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginTop: 8,
                          marginBottom: 8,
                        }}
                      >
                        {rankedProviders.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 12,
                            }}
                          >
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: 9999,
                                background: getProviderColor(p.name),
                                display: "inline-block",
                              }}
                            />
                            {p.name}
                          </div>
                        ))}
                      </div>

                      {/* HINWEISTEXT */}
                      <div
                        style={{
                          fontSize: 12,
                          color: theme.muted,
                          marginBottom: 6,
                        }}
                      >
                        {language === 'de'
                          ? 'Radar zeigt die 8 SOV-Domänen (SOV-1 bis SOV-8) als Achsen mit den Top-3-Anbietern.'
                          : 'Radar shows the 8 SOV domains (SOV-1 through SOV-8) as axes with the top 3 providers.'}
                      </div>
                    </div>
                  </div>

                  {/* Sovereignty Detail Section — full width below charts */}
                  {sovereigntyResults.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: theme.text }}>
                        {language === 'de' ? 'Sovereignty Scoring Details' : 'Sovereignty Scoring Details'}
                      </div>
                      {sovereigntyResults.map((sr, idx) => {
                        const overallSealLabel = SEAL_LABELS[sr.overallSealLevel]
                          ? SEAL_LABELS[sr.overallSealLevel][language] || SEAL_LABELS[sr.overallSealLevel].en
                          : `SEAL-${sr.overallSealLevel}`;
                        // Determine domain weights based on framework mode
                        let dWeights: Record<string, number> = {};
                        if (frameworkMode === 'NO_FRAMEWORK_C5') {
                          dWeights = { 'SOV-1': 0.125, 'SOV-2': 0.125, 'SOV-3': 0.125, 'SOV-4': 0.125, 'SOV-5': 0.125, 'SOV-6': 0.125, 'SOV-7': 0.125, 'SOV-8': 0.125 };
                        } else if (frameworkMode === 'C3A') {
                          dWeights = profilesData?.profiles?.['DE-C3A']?.domainWeights ?? {};
                        } else {
                          dWeights = profilesData?.profiles?.['EU-CSF']?.domainWeights ?? {};
                        }
                        return (
                          <div key={sr.id} style={{ border: `1px solid ${theme.border}`, borderRadius: 6, padding: 12, marginBottom: 12, background: idx === 0 ? 'rgba(247, 103, 94, 0.03)' : '#fff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ width: 22, height: 22, borderRadius: 9999, background: getProviderColor(sr.name), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>{idx + 1}</span>
                                <span style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>{sr.name}</span>
                                {sr.disqualified && !sr.hardRequirementFailures && <span style={{ fontSize: 11, color: '#b91c1c', background: '#fef2f2', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{language === 'de' ? 'Cloud Act disqualifiziert' : 'Cloud Act disqualified'}</span>}
                                {sr.disqualified && sr.hardRequirementFailures && sr.hardRequirementFailures.length > 0 && (
                                  <span style={{ fontSize: 11, color: '#b91c1c', background: '#fef2f2', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                    {language === 'de' ? 'Ausgeschlossen' : 'Excluded'}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Hard requirement failures detail */}
                            {sr.hardRequirementFailures && sr.hardRequirementFailures.length > 0 && (
                              <div style={{ marginBottom: 10, padding: '6px 10px', background: '#fef2f2', borderRadius: 4, border: '1px solid #fecaca' }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', marginBottom: 4 }}>
                                  {frameworkMode === 'NO_FRAMEWORK_C5'
                                    ? (language === 'de' ? 'Security-Mindestanforderung nicht erfüllt (SOV-7 < 7)' : 'Security minimum requirement not met (SOV-7 < 7)')
                                    : (language === 'de' ? 'Mindestanforderung nicht erfüllt' : 'Minimum requirement not met')}
                                </div>
                                <div style={{ fontSize: 11, color: '#991b1b' }}>
                                  {sr.hardRequirementFailures.join(', ')}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                              <div style={{ flex: '1 1 180px', border: `1px solid ${theme.border}`, borderRadius: 4, padding: 10, background: theme.cardBg }}>
                                <div style={{ fontSize: 11, color: theme.muted, marginBottom: 3 }}>Sovereignty Score</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                  <span style={{ fontSize: 24, fontWeight: 800, color: theme.text }}>{sr.sovereigntyScore.toFixed(1)}</span>
                                  <span style={{ fontSize: 13, color: theme.muted }}>/10</span>
                                  <span style={{ fontSize: 13, color: theme.muted, marginLeft: 2 }}>({Math.round(sr.sovereigntyScorePercent)}%)</span>
                                </div>
                                <div style={{ marginTop: 4 }}><SealBadge level={sr.overallSealLevel} label={overallSealLabel} /></div>
                              </div>
                              <div style={{ flex: '1 1 180px', border: `1px solid ${theme.border}`, borderRadius: 4, padding: 10, background: theme.cardBg }}>
                                <div style={{ fontSize: 11, color: theme.muted, marginBottom: 3 }}>Business Score</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                  <span style={{ fontSize: 24, fontWeight: 800, color: theme.text }}>{sr.businessScore.toFixed(1)}</span>
                                  <span style={{ fontSize: 13, color: theme.muted }}>/10</span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: theme.text }}>{language === 'de' ? 'Domänen-Scores' : 'Domain Scores'}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 6 }}>
                                {SOV_DOMAINS.map((domain) => {
                                  const domainScore = sr.domainScores[domain] ?? 0;
                                  const domainSealLevel = sr.sealLevels[domain] ?? 0;
                                  const domainLabel = SOV_DOMAIN_LABELS[domain] ? SOV_DOMAIN_LABELS[domain][language] || SOV_DOMAIN_LABELS[domain].en : domain;
                                  const domainSealLabel = SEAL_LABELS[domainSealLevel] ? SEAL_LABELS[domainSealLevel][language] || SEAL_LABELS[domainSealLevel].en : `SEAL-${domainSealLevel}`;
                                  const weight = dWeights[domain] ?? 0;
                                  let note: string | undefined;
                                  if (frameworkMode === 'C3A' && weight === 0) {
                                    if (domain === 'SOV-7') note = language === 'de' ? 'C5 Voraussetzung' : 'C5 Prerequisite';
                                    else if (domain === 'SOV-8') note = language === 'de' ? 'Außerhalb BSI-Verantwortung' : 'Outside BSI Scope';
                                  }
                                  return <DomainScoreCard key={domain} domain={domain} domainLabel={domainLabel} score={domainScore} sealLevel={domainSealLevel} sealLabel={domainSealLabel} weight={weight} note={note} />;
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* UNTERHALB der Charts: Heatmap grouped by SOV domain */}
                  {view === "heatmap" &&
                    (() => {
                      // Build heatmap data grouped by SOV domain
                      const domainGroups = groupBySovDomain(metrics);
                      const domainOrder = [...SOV_DOMAINS as readonly string[]];
                      // Add "NONE" group for business metrics at the end
                      if (domainGroups['NONE']?.length) {
                        domainOrder.push('NONE');
                      }

                      const heatmapData = domainOrder
                        .filter(domain => domainGroups[domain]?.length)
                        .map(domain => {
                          const domainMetrics = domainGroups[domain] || [];
                          const isBusinessGroup = domain === 'NONE';
                          const categoryLabel = isBusinessGroup
                            ? 'Business'
                            : (SOV_DOMAIN_LABELS[domain]
                              ? `${domain}: ${SOV_DOMAIN_LABELS[domain][language] || SOV_DOMAIN_LABELS[domain].en}`
                              : domain);

                          // Build domain score per provider (for summary row)
                          const domainScoreMap: Record<string, number> = {};
                          if (!isBusinessGroup) {
                            activeProviders.forEach(p => {
                              domainScoreMap[p.id] = computeDomainScore(domainMetrics, p.scores, weights);
                            });
                          }

                          return {
                            category: categoryLabel,
                            domainKey: isBusinessGroup ? null : domain,
                            domainScore: isBusinessGroup ? undefined : domainScoreMap,
                            metrics: domainMetrics.map(m => ({
                              metric: m,
                              scores: activeProviders.map(p => ({
                                providerId: p.id,
                                providerName: p.name,
                                score: p.scores[m.id] ?? 0,
                              })),
                            })),
                          };
                        });

                      return (
                        <div style={{ marginTop: 12 }}>
                          {heatmapData.map((cat) => {
                            const isCollapsedGroup = collapsed[cat.category];
                            return (
                              <div
                                key={cat.category}
                                style={{
                                  marginBottom: 16,
                                  border: `1px solid ${theme.border}`,
                                  borderRadius: 6,
                                  overflow: 'hidden',
                                }}
                              >
                                {/* Domain Group Header */}
                                <div
                                  onClick={() => setCollapsed(c => ({ ...c, [cat.category]: !c[cat.category] }))}
                                  style={{
                                    padding: '10px 14px',
                                    background: theme.primarySoft,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontWeight: 700,
                                    fontSize: 14,
                                  }}
                                >
                                  <span>{cat.category}</span>
                                  <span style={{ fontSize: 18 }}>{isCollapsedGroup ? '▼' : '▲'}</span>
                                </div>

                                {!isCollapsedGroup && (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table
                                      style={{
                                        borderCollapse: 'collapse',
                                        width: '100%',
                                        minWidth: 680,
                                        fontSize: 12,
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              position: 'sticky',
                                              left: 0,
                                              background: theme.cardBg,
                                              border: `1px solid ${theme.border}`,
                                              padding: 6,
                                              textAlign: 'left',
                                              minWidth: 200,
                                            }}
                                          >
                                            {language === 'de' ? 'Merkmal' : 'Metric'}
                                          </th>
                                          {activeProviders.map(p => (
                                            <th
                                              key={p.id}
                                              style={{
                                                border: `1px solid ${theme.border}`,
                                                padding: 6,
                                                textAlign: 'center',
                                                minWidth: 80,
                                              }}
                                            >
                                              {p.name}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {cat.metrics.map(m => (
                                          <tr key={m.metric.id}>
                                            <td
                                              style={{
                                                position: 'sticky',
                                                left: 0,
                                                background: theme.cardBg,
                                                border: `1px solid ${theme.border}`,
                                                padding: 6,
                                                fontWeight: 500,
                                              }}
                                            >
                                              {getTranslatedString(m.metric.label, language)}
                                            </td>
                                            {m.scores.map(s => (
                                              <td
                                                key={s.providerId}
                                                style={{
                                                  border: `1px solid ${theme.border}`,
                                                  padding: 4,
                                                  textAlign: 'center',
                                                  background: `rgba(255,106,76,${Math.min(1, Math.max(0, s.score / 10)) * 0.25})`,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {s.score}
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                        {/* Domain score summary row */}
                                        {cat.domainScore && (
                                          <tr style={{ background: 'rgba(247, 103, 94, 0.06)' }}>
                                            <td
                                              style={{
                                                position: 'sticky',
                                                left: 0,
                                                background: 'rgba(247, 103, 94, 0.06)',
                                                border: `1px solid ${theme.border}`,
                                                padding: 6,
                                                fontWeight: 700,
                                                fontStyle: 'italic',
                                              }}
                                            >
                                              {language === 'de' ? 'Ø Domänen-Score' : 'Ø Domain Score'}
                                            </td>
                                            {activeProviders.map(p => (
                                              <td
                                                key={p.id}
                                                style={{
                                                  border: `1px solid ${theme.border}`,
                                                  padding: 4,
                                                  textAlign: 'center',
                                                  fontWeight: 700,
                                                  background: `rgba(255,106,76,${Math.min(1, Math.max(0, (cat.domainScore![p.id] ?? 0) / 10)) * 0.25})`,
                                                }}
                                              >
                                                {(cat.domainScore![p.id] ?? 0).toFixed(1)}
                                              </td>
                                            ))}
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div
                            style={{
                              fontSize: 12,
                              color: theme.muted,
                              marginTop: 6,
                              paddingLeft: 2,
                            }}
                          >
                            {language === 'de'
                              ? 'Scores je Merkmal (0–10) gruppiert nach SOV-Domäne. Sattere Farbe = höherer Score.'
                              : 'Scores per metric (0–10) grouped by SOV domain. Deeper color = higher score.'}
                          </div>
                        </div>
                      );
                    })()}
                </>
              );
            })()}
          </div>

          {/* Questionnaire-Box (Gewichte) – rechts auf Desktop, unten auf Mobile */}
          <div
            className="card"
            style={{
              border: `1px solid ${theme.border}`,
              borderRadius: 3,
              padding: 12,
              background: theme.cardBg,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Ihre Anforderungen (Gewicht 1–5)
            </div>

            {!metrics.length && (
              <div style={{ fontSize: 15, color: theme.muted }}>
                Lege <b>metrics.json</b> und <b>providers.json</b> in{" "}
                <code>/public</code> ab und klicke „JSON neu laden“.
              </div>
            )}

            {!!metrics.length &&
              (() => {
                const groups = groupByCategory(metrics);
                const cats = Object.keys(groups);
                return (
                  <div>
                    {cats.map((cat) => (
                      <div
                        key={cat}
                        style={{
                          border: `1px solid ${theme.border}`,
                          borderRadius: 3,
                          marginBottom: 10,
                        }}
                      >
                        <div
                          onClick={() =>
                            setCollapsed((c) => ({
                              ...c,
                              [cat]: !c[cat],
                            }))
                          }
                          style={{
                            padding: "12px",
                            background: theme.primarySoft,
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: `1px solid ${theme.primaryBorder}`,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                          >
                            {cat}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: theme.muted,
                            }}
                          >
                            {collapsed[cat] ? "anzeigen" : "verbergen"}
                          </div>
                        </div>
                        {!collapsed[cat] && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 10,
                              padding: 12,
                            }}
                          >
                            {groups[cat].map((m) => (
                              <div
                                key={m.id}
                                style={{
                                  border: `1px solid ${theme.border}`,
                                  borderRadius: 3,
                                  padding: 10,
                                  background: theme.cardBg,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontSize: 15,
                                    fontWeight: 600,
                                    marginBottom: 4,
                                  }}
                                >
                                  <span>{getTranslatedString(m.label, language)}</span>
                                  <button
                                    onClick={() => setInfoMetric(m)}
                                    title="Mehr Infos"
                                    aria-label={`Info zu ${getTranslatedString(m.label, language)}`}
                                    style={{
                                      border: `1px solid ${theme.border}`,
                                      borderRadius: 9999,
                                      width: 22,
                                      height: 22,
                                      lineHeight: "20px",
                                      textAlign: "center",
                                      background: "#fff",
                                      cursor: "pointer",
                                      color: theme.muted,
                                      fontSize: 14,
                                    }}
                                  >
                                    i
                                  </button>
                                </div>
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: theme.muted,
                                    marginBottom: 6,
                                  }}
                                >
                                  {getTranslatedString(m.question, language)}
                                </div>
                                <input
                                  type="range"
                                  min={1}
                                  max={5}
                                  step={1}
                                  value={
                                    weights[m.id] ??
                                    clamp(m.weightDefault ?? 3, 1, 5)
                                  }
                                  onChange={(e) =>
                                    setWeights((w) => ({
                                      ...w,
                                      [m.id]: Number(e.target.value),
                                    }))
                                  }
                                  className="weight-slider"
                                  style={{ width: "100%" }}
                                />
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: theme.muted,
                                    textAlign: "right",
                                  }}
                                >
                                  Gewicht: {weights[m.id]}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
          </div>
        </div>
      </div>

      {/* ===== INFO-DIALOG ===== */}
      {infoMetric && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 60,
          }}
        >
          <div
            style={{
              width: "min(720px, 92vw)",
              background: theme.cardBg,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: `1px solid ${theme.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{getTranslatedString(infoMetric.label, language)}</div>
                <div style={{ fontSize: 12, color: theme.muted, marginTop: 4 }}>
                  Kategorie: {infoMetric.category}
                </div>
              </div>
              <button
                onClick={() => setInfoMetric(null)}
                style={{
                  border: `1px solid ${theme.border}`,
                  borderRadius: 3,
                  padding: "6px 10px",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Schließen
              </button>
            </div>

            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 15, lineHeight: 1.5 }}>
                {(infoMetric.description && infoMetric.description.trim().length > 0)
                  ? infoMetric.description
                  : infoMetric.question}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== FOOTER ===== */}
      <footer
        style={{
          marginTop: 16,
          paddingTop: 8,
          paddingBottom: 12,
          borderTop: `1px solid ${theme.border}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          lineHeight: 1.3,
          color: theme.muted,
        }}
      >
        <div
          style={{
            background: "rgba(0,0,0,0.04)",
            color: theme.text,
            border: `1px solid ${theme.border}`,
            padding: "6px 10px",
            borderRadius: 3,
            fontSize: 12,
            letterSpacing: 0.2,
          }}
        >
          Powered by{" "}
          <a
            href="https://www.crayon.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.text, textDecoration: "underline", margin: "0 4px" }}
          >
            Crayon
          </a>
          &nbsp;&amp;&nbsp;
          <a
            href="https://www.softwareone.com/de-de"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.text, textDecoration: "underline", margin: "0 4px" }}
          >
            SoftwareOne
          </a>
        </div>

        <a href="/impressum.html" style={{ color: theme.text, textDecoration: "none" }}>
          Impressum
        </a>
      </footer>

      {/* ===== Email Report Modal ===== */}
      <EmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailReportSubmit}
      />

      {/* ===== HIDDEN CAPTURE AREA (for PDF) ===== */}
      <div style={{ position: "absolute", left: -99999, top: -99999 }}>
        {/* Hidden Bars for PDF */}
        <div
          ref={barCaptureRef}
          style={{
            width: 900,
            height: 260,
            background: "#fff",
            padding: 12,
            border: `1px solid ${theme.border}`,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={results} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <XAxis dataKey="name" hide />
              <YAxis domain={[0, 100]} hide />
              <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                {results.map((r) => (
                  <Cell key={r.id} fill={getProviderColor(r.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hidden Radar for PDF */}
        <div
          ref={radarCaptureRef}
          style={{
            width: 900,
            height: 340,
            background: "#fff",
            padding: 12,
            marginTop: 16,
            border: `1px solid ${theme.border}`,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarForCapture.radarData}>
              <PolarGrid stroke={theme.border} />
              <PolarAngleAxis dataKey="metric" tick={{ fill: theme.text, fontSize: 12 }} />
              <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} stroke={theme.border} />
              {radarForCapture.rankedProviders.map((p) => (
                <Radar
                  key={p.id}
                  name={p.name}
                  dataKey={p.name}
                  stroke={getProviderColor(p.name)}
                  fill={getProviderColor(p.name)}
                  fillOpacity={theme.radarFillOpacity}
                  strokeOpacity={0.9}
                />
              ))}
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      </div>
    </div>
  );
}